// ────────────────────────────────────────────────────────────────────────────
// Page arithmetic shared by the renderer and the inspector.
//
// The line table is one flexible column (Description) plus a set of fixed
// widths. Nothing stops an operator ticking every column until the fixed
// widths eat the whole page and Description is squeezed to a single character
// per line — so the width left over is computed here, shown in the inspector
// as a live figure, and floored in the renderer.
// ────────────────────────────────────────────────────────────────────────────
import type { LineColumn, PageSettings, PageSize } from './types';

/** Media width in points for each supported page size. */
const PAGE_WIDTH_PT: Record<PageSize, number> = {
  A4: 595.28,
  LETTER: 612,
};

/**
 * Narrowest the flexible column is allowed to get. Below roughly this width a
 * charge description breaks mid-word on every line, which is unreadable at any
 * font size.
 */
export const MIN_FLEX_COLUMN_PT = 90;

/** Media height in points — the width when the page is turned landscape. */
const PAGE_HEIGHT_PT: Record<PageSize, number> = {
  A4: 841.89,
  LETTER: 792,
};

/** Printable width between the margins, honouring orientation. */
export function contentWidthPt(page: PageSettings): number {
  const media =
    page.orientation === 'landscape' ? PAGE_HEIGHT_PT[page.size] : PAGE_WIDTH_PT[page.size];
  return media - page.marginPt * 2;
}

export interface ColumnFit {
  /** Total width claimed by the fixed-width columns. */
  fixedTotalPt: number;
  /** Printable width available to the whole table. */
  availablePt: number;
  /** What is left for the flexible column. Can go negative. */
  flexRemainingPt: number;
  /** True when the flexible column is squeezed past readability. */
  tooNarrow: boolean;
}

/**
 * How the visible columns fit the page. `flexKey` is the column that stretches
 * (Description when visible, otherwise the first visible column).
 */
export function measureColumns(
  columns: LineColumn[],
  page: PageSettings,
  flexKey: LineColumn['key'],
): ColumnFit {
  const visible = columns.filter((c) => c.visible);
  const fixedTotalPt = visible
    .filter((c) => c.key !== flexKey)
    .reduce((sum, c) => sum + c.widthPt, 0);
  const availablePt = contentWidthPt(page);
  const flexRemainingPt = availablePt - fixedTotalPt;
  return {
    fixedTotalPt,
    availablePt,
    flexRemainingPt,
    tooNarrow: flexRemainingPt < MIN_FLEX_COLUMN_PT,
  };
}

/**
 * The column that stretches to fill leftover width.
 *
 * `widthPt <= 0` is the explicit "fills width" marker — that is what the
 * inspector shows for the stretch column, so honouring it first keeps the two
 * in agreement. Only if nothing is marked do we fall back to the columns that
 * are naturally prose (`details`, then `description`), and last of all to the
 * first visible column so a table of nothing but fixed columns still spans the
 * page.
 *
 * Getting this wrong is not cosmetic: a zero-width column that is NOT the flex
 * key collapses to nothing, which is exactly how the prose column once ended up
 * a one-character sliver while Date stretched.
 */
export function resolveFlexKey(columns: LineColumn[]): LineColumn['key'] | null {
  const visible = columns.filter((c) => c.visible);
  const marked = visible.find((c) => c.widthPt <= 0);
  if (marked) return marked.key;
  const prose = visible.find((c) => c.key === 'description');
  return prose?.key ?? visible[0]?.key ?? null;
}
